import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";

import { useGetProductById } from "../../../api/product";
import {
  useGetAverageProductSummary,
  useGetProductSummary,
  useGetProductSummaryHistory,
  useGetProductSummaryHistoryDropDownData
} from "../../../api/productSummary";
import { CommonModal, DropDown, InputText, TabView } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import MainBottomButtonView from "../../../components/mainBottomButtonView/MainBottomButtonView";
import {
  DISABLE_IS_ROOT_PRODUCT_BUTTON,
  PRODUCT_TAB_KEYS,
  ProductViewTabs
} from "../../../config/constants";
import { IsRootProduct } from "../../../config/enums/ProductType.enum";
import ProductDetailSummery from "./productViewTab/ProductDetailsSummery";
import ProductImpactTab from "./productViewTab/ProductImpactTab";
import ProductSummaryHistory from "./productViewTab/ProductSummaryHistory";
import ProductVolumeAndCost from "./productViewTab/ProductVolumeAndCost";

import "./productDetails.scss";

const ProductDetailModal = ({ visibleModal, setVisibleModal, productId, openEditDetails }: any) => {
  const [selectedTab, setSelectedTab] = useState<Number>(0);
  const [defaultSalesUnit, setDefaultSalesUnit] = useState<string>("");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  const { data: productDetails, isLoading: productDetailsLoading } = useGetProductById(productId);

  const productIsAverage = !DISABLE_IS_ROOT_PRODUCT_BUTTON
    ? productDetails?.is_average_product || false
    : false;
  const productIsAverageResult = productIsAverage ? IsRootProduct.YES : IsRootProduct.NO;

  const productSummaryData = useGetProductSummary({
    productId,
    salesUnitId: defaultSalesUnit,
    productIsAverage
  });
  const { data: productSummaryDropdownData, isFetching: isProductSummaryHistoryLoading } =
    useGetProductSummaryHistoryDropDownData({
      productId,
      salesUnitId: defaultSalesUnit,
      productIsAverage
    });

  const productAverageSummaryData = useGetAverageProductSummary({
    productId,
    productIsAverage: productIsAverage
  });
  const currentProductSummeryData = useMemo(() => {
    if (productIsAverage) return productAverageSummaryData;
    return productSummaryData;
  }, [productSummaryData, productAverageSummaryData, productIsAverage]);

  const values = useMemo(() => {
    return {
      product_name: productDetails?.product_name,
      registration_number: productDetails?.registration_number,
      product_other_info: productDetails?.product_other_info,
      product_type: productDetails?.product_type
    };
  }, [productDetails]);

  const productSummaryHistoryData = useGetProductSummaryHistory({
    pageNumber: pageNumber,
    pageSize: pageSize,
    productId,
    salesUnitId: defaultSalesUnit
  });

  const currentSalesUnitData = useMemo(() => {
    if (productDetails) {
      let salesUnitData = productDetails?.sales_units.reduce((currentType: any, data: any) => {
        let salesUnit = {
          key: data?._id,
          text: data?.sales_unit_name,
          value: data?._id
        };
        return [...currentType, salesUnit];
      }, []);
      return salesUnitData;
    }
    return [];
  }, [productDetails]);

  const selectedUnitSize = useMemo(() => {
    if (productDetails && defaultSalesUnit) {
      const selectedUnit = productDetails?.sales_units.find(
        (unit: any) => unit._id === defaultSalesUnit
      );
      return parseFloat(selectedUnit?.sales_unit_size) || 1;
    }
    return 1;
  }, [productDetails, defaultSalesUnit]);

  const selectedUnitType = useMemo(() => {
    if (productDetails && defaultSalesUnit) {
      const selectedUnit = productDetails?.sales_units.find(
        (unit: any) => unit._id === defaultSalesUnit
      );
      return selectedUnit?.sales_unit_type || 'kg';
    }
    return 'kg';
  }, [productDetails, defaultSalesUnit]);

  useEffect(() => {
    setDefaultSalesUnit(productDetails?.sales_units[0]._id);
  }, [productDetails]);

  const changeSalesUnitId = (value: string) => {
    setDefaultSalesUnit(value);
    setPageNumber(1);
    setPageSize(5);
  };

  const {
    register,
    formState: { errors }
  } = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const renderTabContent = () => {
    switch (selectedTab) {
      case PRODUCT_TAB_KEYS.SUMMARY:
        return (
          <ProductDetailSummery
            productDetails={productDetails}
            defaultSalesUnit={defaultSalesUnit}
            productIsAverage={productIsAverageResult}
            productIsAverageStatus={productIsAverage}
          />
        );
      case PRODUCT_TAB_KEYS.ENVIRONMENTAL_FOOTPRINT:
        return (
          <ProductImpactTab
            productDetails={productDetails}
            productSummaryDropdownData={productSummaryDropdownData}
            isProductSummaryHistoryLoading={isProductSummaryHistoryLoading}
          />
        );
      case PRODUCT_TAB_KEYS.HISTORY:
        return (
          <ProductSummaryHistory
            productDetails={productDetails}
            productSummaryHistoryData={productSummaryHistoryData}
            pageNumber={pageNumber}
            pageSize={pageSize}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            productId={productId}
            salesUnitId={defaultSalesUnit}
            productIsAverage={productIsAverage}
          />
        );
      case PRODUCT_TAB_KEYS.ANNUAL_VOLUME_AND_COST:
        return <ProductVolumeAndCost productId={productId} salesUnitId={defaultSalesUnit} unitSize={selectedUnitSize} unitType={selectedUnitType} />;
      default:
        return null;
    }
  };

  if (
    productDetailsLoading ||
    productSummaryData.isFetching ||
    productSummaryHistoryData.isFetching
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => setVisibleModal(false)}
        centered={true}
        title="Product Details"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16} className="p-0">
            <div className="productDetailsInsert">
              <form>
                <CommonSplitContainer
                  topChild={
                    <>
                      <Grid className="productDetailsViewMain mb-1">
                        <Grid.Column
                          computer={8}
                          tablet={16}
                          mobile={16}
                          className="paddingRemoveBottom"
                        >
                          <InputText
                            register={register}
                            errors={errors.product_name}
                            labelName={"Product Name"}
                            placeholder="Name"
                            name="product_name"
                            disabled={true}
                          />
                        </Grid.Column>
                        <Grid.Column
                          computer={8}
                          tablet={16}
                          mobile={16}
                          className="paddingRemoveBottom"
                        >
                          <DropDown
                            required={true}
                            labelName={"Sales Unit"}
                            currentData={currentSalesUnitData}
                            defaultValue={defaultSalesUnit}
                            customGridColumn={"customGridColomnType"}
                            handleChangeState={(e: any, { value }: any) => {
                              changeSalesUnitId(value);
                            }}
                          />
                        </Grid.Column>
                      </Grid>
                      <TabView
                        tabData={ProductViewTabs}
                        computer={2}
                        tablet={16}
                        mobile={16}
                        defaultSelect={selectedTab}
                        customMainTab="ml-0"
                        disable={false}
                        onChange={(index: number) => setSelectedTab(index)}
                      />
                      {renderTabContent()}
                    </>
                  }
                  bottomChild={
                    <MainBottomButtonView
                      cancelButtonStatus
                      saveButtonStatus
                      saveTitle={"Edit"}
                      cancelButton={() => setVisibleModal(false)}
                      saveButton={() => {
                        openEditDetails(true);
                      }}
                    />
                  }
                />
              </form>
            </div>
          </Grid.Column>
        </Grid>
      </CommonModal>
    </>
  );
};

export default ProductDetailModal;
